import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { createMultipleTeamResource } from 'src/services/projectService';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationModal from 'src/content/common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import moment from 'moment';
import { DatePicker } from '@mui/lab';
import 'src/content/projectDetails/ProjectDetails.css';
import { getCalendarMonthInRange } from './calendarMonth';

const AllocateForm = ({
  open,
  toggle,
  getData,
  data,
  allEmployees,
  setSnackBarOpen,
  setSnackbarSeverity,
  setSnackBarMessage,
  projectDetails
}) => {
  const [allocateData, setAllocateData] = useState([]);
  const [allocateDataErrors, setAllocateDataErrors] = useState([]);

  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [allocation, setAllocation] = useState('');

  const [selectedResourceInfo, setSelectedResourceInfo] = useState<any>({});

  const onClose = () => {
    setAllocateData([]);
    setAllocateDataErrors([]);
    toggle();
    setAllocation('');
  };

  const [allAllocation, setAllAllocation] = useState(100);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [resourceEndDate, setResourceEndDate] = useState(null);
  const [roleAllocation, setRoleAllocation] = useState(null);

  useEffect(() => {
    setAllocateData(
      data.map((item) => {
        setAllAllocation(item?.allocation);
        setStartDate(new Date(item?.startDate));
        setEndDate(item?.endDate);
        setResourceEndDate(item?.resourceAllocations[0]?.endDate ? new Date(item?.resourceAllocations[0]?.endDate) : null);
        return {
          projectRoleCode: item['code']
        };
      })
    );
  }, [data, open]);

  const dispatch = useDispatch();

  const toggleConfirmation = () => {
    const errors: any = [{}];

    if (openConfirmation) {
      setOpenConfirmation(false);
    } else {
      if (allocateData?.length > 0) {
        allocateData.forEach((allo, index) => {
          errors[index] = {};
          if (!allo?.name) {
            errors[index].empError = 'Resource name is required.';
          } if (!allo?.type) {
            errors[index].empTypeError = 'Resource type is required.';
          }if (!allo?.allocation) {
            errors[index].empAllocationError =
              'Resource Allocation is required.';
          }else if(roleAllocation < allo?.allocation) {
            errors[index].empAllocationError =
            'Resource Allocation should be equal or less than to the Role Allocation percentage.'
          }
          if (!allo?.startDate) {
            errors[index].empStartDateError =
              'Resource start date is required.';
          } if (!allo?.endDate) {
            errors[index].empEndDateError = 'Resource end date is required.';
          } else if (Date.parse(allo.startDate) > Date.parse(allo?.endDate)) {
            errors[index].empEndDateError =
              'End date should be greater than start date';
          }
        });

        const errorsAvailable = errors.filter(
          (fil) => Object.keys(fil).length !== 0
        );

        if (errorsAvailable.length > 0) {
          setAllocateDataErrors(errors);
        } else {
          setOpenConfirmation(true);
        }
      }
    }
  };

  const onSubmitForm = () => {
    dispatch(AppActions.showLoader());
    if (allocateData?.length > 0) {
      createMultipleTeamResource(allocateData)
        .then(() => {
          getData();
          onClose();
          toggleConfirmation();
          setSnackBarMessage(
            'You have updated the resource allocation details successfully.'
          );
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          setSnackBarMessage(error?.response?.data ? error?.response?.data : 'Unable allocated resources.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          dispatch(AppActions.hideLoader());
        });
    }
  };

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  const [selectedOption, setSelectedOption] = useState('');
  const [searchText, setSearchText] = useState('');

  const displayedOptions = useMemo(
    () =>
      allEmployees.length > 0 &&
      allEmployees?.filter((option) => containsText(option.name, searchText) && !option?.employeeFinance?.isResign),
    [searchText, allEmployees]
  );
  const { isLoading } = useAppSelector((state) => state.appReducer);

  return (
    <Grid sx={{ p: 2 }}>
      <Typography sx={{ mb: 0 }} variant="h4" component="h4">
        Allocate Resource
      </Typography>
      {data?.length > 0 &&
        allocateData?.length > 0 &&
        data.map((dat, index) => (
          <Grid container key={dat.code}>
            <Grid item xs={12}>
              <Typography
                style={{ fontSize: '16px', marginTop: '1rem' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                {dat.role}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                Resource Name
              </Typography>
              <Select
                id={"allocationData-employeeId" + index}
                MenuProps={{ autoFocus: false }}
                labelId="search-select-label"
                style={{
                  fontSize: '11px',
                  width: '95%'
                }}
                size="small"
                displayEmpty
                value={allocateData[index]?.employeeId || ''}
                onChange={(e) => {
                  setSelectedOption(e.target.value);
                  const id = e.target.value;
                  const filteredEmp = allEmployees.filter(
                    (emp) => emp.employeeId === id
                  )[0];

                  setSelectedResourceInfo(filteredEmp);

                  let items = [...allocateData];
                  let item = { ...items[index] };
                  item = {
                    ...item,
                    name: filteredEmp.name,
                    employeeId: e.target.value,
                  };

                  items[index] = item;

                  setAllocateData(items);

                  setRoleAllocation(dat.allocation)

                  let errorItems = [...allocateDataErrors];
                  let errorItem = { ...errorItems[index] };

                  errorItem = {
                    empError: ''
                  };

                  errorItems[index] = errorItem;

                  setAllocateDataErrors(errorItems);
                }}
                onClose={() => setSearchText('')}
              >
                <ListSubheader>
                  <TextField
                    id={"search-allocationData-employeeId" + index}
                    size="small"
                    autoFocus
                    placeholder="Type to search..."
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        // Prevents autoselecting item while typing (default Select behaviour)
                        e.stopPropagation();
                      }
                    }}
                  />
                </ListSubheader>
                <MenuItem
                  style={{ display: 'none' }}
                  selected
                  disabled
                  value={''}
                >
                  Select Resource
                </MenuItem>
                {displayedOptions.length > 0 &&
                  displayedOptions?.map((option, i) => (
                    <MenuItem key={i} value={option.employeeId}>
                      {option.name}
                    </MenuItem>
                  ))}
              </Select>
              {allocateDataErrors[index]?.empError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                Resource Type
              </Typography>
              <Select
                id={"allocationData-name" + index}
                disabled={!allocateData[index]?.name}
                size="small"
                displayEmpty
                defaultValue={''}
                inputProps={{ 'aria-label': 'Without label' }}
                style={{
                  fontSize: '11px',
                  width: '95%'
                }}
                value={allocateData[index]?.type}
                onChange={(e) => {
                  let items = [...allocateData];
                  let item = { ...items[index] };
                  item = {
                    ...item,
                    type: e.target.value
                  };

                  items[index] = item;

                  setAllocateData(items);

                  let errorItems = [...allocateDataErrors];
                  let errorItem = { ...errorItems[index] };

                  errorItem = {
                    ...errorItem,
                    empTypeError: ''
                  };

                  errorItems[index] = errorItem;

                  setAllocateDataErrors(errorItems);
                }}
              >
                <MenuItem
                  style={{ display: 'none' }}
                  selected
                  disabled
                  value=""
                >
                  Select Resource Type
                </MenuItem>
                <MenuItem value="Billable">Billable</MenuItem>
                <MenuItem value="Non-Billable">Non-Billable</MenuItem>
                <MenuItem value="Shadow">Shadow</MenuItem>
                <MenuItem value="Support">Support</MenuItem>
                <MenuItem value="T&M">T&M</MenuItem>
              </Select>
              {allocateDataErrors[index]?.empTypeError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empTypeError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                Allocation (%)
              </Typography>
              <TextField
                id={"allocationDate-allocation" + index}
                disabled={!allocateData[index]?.name}
                size="small"
                variant="outlined"
                style={{ width: '95%' }}
                inputProps={{
                  max: 100,
                  min: 0,
                  style: {
                    fontSize: '11px'
                  }
                }}
                placeholder="Enter Allocation"
                type="number"
                value={allocateData[index]?.allocation || ''}
                onChange={(e) => {
                  let value = parseInt(e.target.value, 10);

                  let items = [...allocateData];
                  let item = { ...items[index] };
                  item = {
                    ...item,
                    allocation: value
                  };

                  items[index] = item;
                  setAllocateData(items);

                  let errorItems = [...allocateDataErrors];
                  let errorItem = { ...errorItems[index] };

                  if (value > dat.allocation) {
                    errorItem = {
                      ...errorItem,
                      empAllocationError: 'Resource Allocation should be equal or less than to the Role Allocation percentage.'
                    };
                  } else {
                    errorItem = {
                      ...errorItem,
                      empAllocationError: ''
                    };
                  }

                  errorItems[index] = errorItem;
                  setAllocateDataErrors(errorItems);
                }}
              />
              {allocateDataErrors[index]?.empAllocationError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empAllocationError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
    

                Start Date
              </Typography>
              <div style={{ marginRight: 15 }}>
                <FormControl fullWidth>
                  <DatePicker
                    disabled={!allocateData[index]?.name}
                    InputProps={{
                      className: 'date-picker-allocation'
                    }}
                    defaultCalendarMonth={getCalendarMonthInRange(startDate, endDate)}
                    minDate={new Date(startDate) ? new Date(startDate) : 
                      new Date(startDate) > new Date(selectedResourceInfo?.joinedDate) ? 
                      new Date(startDate) : new Date(selectedResourceInfo?.joinedDate) 
                    }
                    maxDate={selectedResourceInfo?.employeeFinance?.lastWorkingDate ? 
                      new Date(endDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                      new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(endDate) : 
                      new Date(endDate)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id={'allocationData-startDate' + index}
                      />
                    )}
                    value={
                      allocateData[index]?.startDate
                        ? allocateData[index].startDate
                        : ''
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        let items = [...allocateData];
                        let item = { ...items[index] };
                        item = {
                          ...item,
                          startDate: moment(newValue).format('YYYY-MM-DD')
                        };

                        items[index] = item;

                        setAllocateData(items);

                        let errorItems = [...allocateDataErrors];
                        let errorItem = { ...errorItems[index] };

                        errorItem = {
                          ...errorItem,
                          empStartDateError: ''
                        };

                        errorItems[index] = errorItem;

                        setAllocateDataErrors(errorItems);
                      }
                    }}
                  />
                </FormControl>
              </div>
              {allocateDataErrors[index]?.empStartDateError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empStartDateError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                End Date
              </Typography>
              <div style={{ marginRight: 15 }}>
                <FormControl fullWidth>
                  <DatePicker
                    disabled={!allocateData[index]?.startDate}
                    InputProps={{
                      className: 'date-picker-allocation'
                    }}
                    minDate={new Date(allocateData[index]?.startDate) ? new Date(allocateData[index]?.startDate) :
                      new Date(allocateData[index]?.endDate) > new Date(selectedResourceInfo?.joinedDate) ? 
                      new Date(allocateData[index]?.endDate) : new Date(selectedResourceInfo?.joinedDate)}
                    maxDate={selectedResourceInfo?.employeeFinance?.lastWorkingDate ? 
                      new Date(endDate) > new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) ? 
                      new Date(selectedResourceInfo?.employeeFinance?.lastWorkingDate) : new Date(endDate) : 
                      new Date(endDate)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id={'allocationData-endDate' + index}
                      />
                    )}
                    value={
                      allocateData[index]?.endDate
                        ? allocateData[index].endDate
                        : ''
                    }
                    onChange={(newValue) => {
                      if (newValue) {
                        let items = [...allocateData];
                        let item = { ...items[index] };
                        item = {
                          ...item,
                          endDate: moment(newValue).format('YYYY-MM-DD')
                        };

                        items[index] = item;

                        setAllocateData(items);

                        let errorItems = [...allocateDataErrors];
                        let errorItem = { ...errorItems[index] };

                        errorItem = {
                          ...errorItem,
                          empEndDateError: ''
                        };

                        errorItems[index] = errorItem;

                        setAllocateDataErrors(errorItems);
                      }
                    }}
                  />
                </FormControl>
              </div>
              {allocateDataErrors[index]?.empEndDateError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empEndDateError}
                </Typography>
              )}
            </Grid>
       {/*     <Grid item xs={4} sx={{ mt: 2 }}>
              <Typography
                style={{ fontSize: '12px' }}
                variant="h4"
                component="h4"
                fontWeight="light"
              >
                Resource Status
              </Typography>
              <Select
                disabled={!allocateData[index]?.name}
                size="small"
                displayEmpty
                defaultValue={''}
                inputProps={{ 'aria-label': 'Without label' }}
                style={{
                  fontSize: '11px',
                  width: '95%'
                }}
                value={allocateData[index]?.status}
                onChange={(e) => {
                  let items = [...allocateData];
                  let item = { ...items[index] };
                  item = {
                    ...item,
                    status: e.target.value
                  };

                  items[index] = item;

                  setAllocateData(items);

                  let errorItems = [...allocateDataErrors];
                  let errorItem = { ...errorItems[index] };

                  errorItem = {
                    ...errorItem,
                    empStatusError: ''
                  };

                  errorItems[index] = errorItem;

                  setAllocateDataErrors(errorItems);
                }}
              >
                <MenuItem
                  style={{ display: 'none' }}
                  selected
                  disabled
                  value=""
                >
                  Select Status
                </MenuItem>
                <MenuItem value="Allocated">Allocated</MenuItem>
                <MenuItem value="Not-Allocated">Not-Allocated</MenuItem>
              </Select>
              {allocateDataErrors[index]?.empStatusError && (
                <Typography
                  style={{
                    fontSize: '9px',
                    textTransform: 'capitalize'
                  }}
                  color="red"
                >
                  {allocateDataErrors[index]?.empStatusError}
                </Typography>
              )}
            </Grid>*/}
          </Grid>
        ))}
      <Grid
        item
        sx={{
          mt: 3,
          pr: 2,
          display: 'flex',
          justifyContent: 'flex-end'
        }}
        xs={12}
      >
        <Button
          id="btn-onClose"
          sx={{
            width: '10%',
            fontWeight: 'normal',
            background: '#F5F7FF',
            fontSize: '12px'
          }}
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          id="btn-toggleConfirmation"
          onClick={() => toggleConfirmation()}
          sx={{
            ml: 2,
            width: '10%',
            fontSize: '12px',
            fontWeight: 'normal',
            background: '#0069E4'
          }}
          variant="contained"
          type="button"
        >
          Allocate
        </Button>
      </Grid>
      <ConfirmationModal
        isLoading={isLoading}
        open={openConfirmation}
        toggle={toggleConfirmation}
        message={`Are you sure you want to update the resource allocation details?`}
        onConfirm={() => onSubmitForm()}
        noToggle={toggleConfirmation}
      />
    </Grid>
  );
};

export default AllocateForm;
